import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function SignInPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedNext = searchParams.get("next") || "";
  const safeNext = requestedNext.startsWith("/") && !requestedNext.startsWith("//") ? requestedNext : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user && typeof user === "object") {
    return <Navigate to={safeNext || (user.onboarded ? "/vault" : "/initiate/welcome")} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error || "Login failed");
    } else {
      toast.success("Welcome back.");
      if (safeNext) navigate(safeNext, { replace: true });
    }
  };

  return (
    <div
      className="min-h-screen bg-black text-white relative overflow-hidden flex items-center justify-center p-6"
      data-testid="sign-in-page"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/10 via-black to-black" />
      <div
        className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="flex items-center gap-3 mb-10 text-purple-400 group"
          data-testid="sign-in-home-link"
        >
          <img
            src="/stag-vault-lockup-v2.webp"
            alt="Stag Vault · AI · Passive Income"
            className="h-14 w-auto select-none"
            draggable={false}
          />
        </Link>

        <div className="bg-[#0a0a0a]/90 backdrop-blur-xl border border-purple-500/25 p-8 md:p-10 relative" data-testid="login-card">
          <div className="absolute -top-px left-0 w-16 h-px bg-gradient-to-r from-purple-500 to-transparent" />
          <div className="absolute -top-px right-0 w-16 h-px bg-gradient-to-l from-purple-500 to-transparent" />

          <div className="text-purple-400 text-xs tracking-[0.3em] uppercase mb-2">
            Enter
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-8">
            Sign in to the vault
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-zinc-400 text-sm tracking-wide mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@domain.com"
                className="w-full bg-black border border-zinc-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none px-4 py-3 text-base text-white placeholder:text-zinc-700 transition-all"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-zinc-400 text-sm tracking-wide">Password</label>
                <Link
                  to="/auth/forgot"
                  className="text-purple-400 hover:text-purple-300 text-xs tracking-wide underline-offset-4 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot?
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-black border border-zinc-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none px-4 py-3 text-base text-white placeholder:text-zinc-700 transition-all"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div
                className="border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-3 text-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-transparent border border-purple-500 text-purple-300 hover:bg-purple-500 hover:text-black px-8 py-4 text-sm tracking-[0.25em] uppercase transition-all duration-300 shadow-[0_0_20px_rgba(168,85,247,0.15)] hover:shadow-[0_0_35px_rgba(168,85,247,0.6)] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-button"
            >
              {submitting ? "Verifying..." : "Enter the vault"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-900 text-center">
            <p className="text-zinc-500 text-sm">
              Not initiated yet?{" "}
              <Link
                to="/initiate"
                className="text-purple-400 hover:text-purple-300 underline-offset-4 hover:underline"
                data-testid="go-to-register-link"
              >
                Request access
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
